/**
 * usePageSize (KAN-731)
 *
 * Номер страницы и размер страницы списка. Выбранный размер запоминается в
 * localStorage отдельно для каждого списка (ключ `pageSize:<listKey>`),
 * по умолчанию 20. Смена размера возвращает на первую страницу.
 * Недоступный localStorage (приватный режим, запрет сайта) не ломает список:
 * размер просто не запоминается.
 */
import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 20;

const storageKey = (listKey: string): string => `pageSize:${listKey}`;

function isAllowedSize(value: number): boolean {
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(value);
}

export function readStoredPageSize(listKey: string): number {
  try {
    const raw = localStorage.getItem(storageKey(listKey));
    const value = raw === null ? NaN : Number(raw);
    return isAllowedSize(value) ? value : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

export function writeStoredPageSize(listKey: string, size: number): void {
  try {
    localStorage.setItem(storageKey(listKey), String(size));
  } catch {
    // Хранилище недоступно: размер действует до перезагрузки страницы.
  }
}

export interface PageSizeState {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  pageSize: number;
  setPageSize: (size: number) => void;
}

export function usePageSize(listKey: string): PageSizeState {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(() => readStoredPageSize(listKey));

  const setPageSize = useCallback(
    (size: number) => {
      setPageSizeState(size);
      setPage(1);
      writeStoredPageSize(listKey, size);
    },
    [listKey]
  );

  return { page, setPage, pageSize, setPageSize };
}

/** Срез страницы для списков, которые целиком приходят на клиент. */
export function paginateLocally<T>(items: readonly T[], page: number, pageSize: number): T[] {
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return items.slice(start, start + pageSize);
}
